import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo, useRef } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { useDebounce } from 'react-use';
import {
    ProductActions,
    ProductDialogs,
    ProductSticky,
    SpiffScript,
    YotpoReviewsSummary,
} from '~/features/product-details/components';
import {
    ProductAIChat,
    ProductBrand,
    ProductCampaignLifeCycle,
    ProductCampaignRedirect,
    ProductCategories,
    ProductCompareButton,
    ProductExpressDelivery,
    ProductFreight,
    ProductGallery,
    ProductInfoLabels,
    ProductPaymentMethods,
    ProductPrelaunchCountdown,
    ProductPrelaunchNotice,
    ProductPrices,
    ProductPromotionCountdown,
    ProductRecommendations,
    ProductSplash,
    ProductTabs,
    ProductTags,
    ProductUSP,
    ProductVariants,
    ProductWishlistButton,
    SpiffPersonalization,
    WishCloudButton,
} from '~/features/product-details/components/ProductOverview/components';
import { usePDPActions, usePDPData } from '~/features/product-details/context';
import { VariantOption } from '~/features/product-details/types';
import { getSalesforceProductImages } from '~/lib/salesforce/utilities/product-images';
import { useSpecialPrice } from '~/services/special-price';
import { Typography } from '~/shared/components';
import { useEvents } from '~/shared/hooks/useEvents';
import { transformSalesforceProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { useGABreadcrumb } from '~/shared/hooks/useGABreadcrumb';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductOverview.module.scss';
import { replaceProductHyphensWithSlashes } from '~/lib/salesforce/utilities/formatters';

const PRODUCT_SUMMARY_ID = 'product-summary';

const focusSummary = () => {
    const summary = document.getElementById(PRODUCT_SUMMARY_ID);
    const candidates =
        summary?.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [];

    const targetElement = Array.from(candidates).find(
        (candidate) => candidate.getClientRects().length > 0,
    );

    targetElement?.focus();
};

const VARIANT_TOAST_OPTIONS: ToastOptions = {
    toastId: 'variantRedirectNotification',
    hideProgressBar: true,
    autoClose: 5000,
    pauseOnHover: false,
    closeOnClick: true,
    theme: 'dark',
    style: { width: 300, alignItems: 'flex-start' },
    bodyStyle: { alignItems: 'flex-start' },
    containerId: 'product-variants-toast',
};

type Translate = ReturnType<typeof useTranslation>['translate'];

const showVariantChangeToast = (translate: Translate) => {
    const title = translate('pdp.variant.changeSelectionTitle');

    toast(
        <div>
            {title && (
                <>
                    <strong>{title}</strong>
                    <br />
                </>
            )}

            {translate('pdp.variant.changeSelectionDescription')}
        </div>,
        VARIANT_TOAST_OPTIONS,
    );
};

export const ProductOverview: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
    className,
    ...rest
}) => {
    const { data: frame } = useFrame();
    const router = useRouter();
    const { translate } = useTranslation();
    const { PDPActionEvent, productDetailviewEvent } = useEvents(frame);
    const gaBreadcrumb = useGABreadcrumb();

    const { product, isCustomizableWithSpiff, isSoldOutPermanently, isSoldOutPermanentlyOnline } =
        usePDPData();
    const { selectVariant } = usePDPActions();

    const { campaignText } = useSpecialPrice({
        id: product.id,
        c_specialPrices: product.c_specialPrices,
    });

    const actionsRef = useRef<HTMLDivElement>(null);

    const images = useMemo(() => getSalesforceProductImages(product), [product]);

    const onVariantClick = useCallback(
        (option: VariantOption, event: React.MouseEvent<HTMLElement>) => {
            // Let CTRL (Windows) or Meta/Option (MacOS) clicks open the variant in a new tab.
            if (event.ctrlKey || event.metaKey) return;

            event.preventDefault();

            if (option.matchQuality === 'none') return;

            if (option.matchQuality === 'closest') showVariantChangeToast(translate);

            selectVariant(option);
        },
        [selectVariant, translate],
    );

    const onCategoryClick = useCallback(
        (category: string) => {
            PDPActionEvent(`Category Click - ${category}`, `${product.id} | ${product.name}`);
        },
        [product.id, product.name],
    );

    useDebounce(
        () => {
            if (!product || !gaBreadcrumb) return;

            productDetailviewEvent(transformSalesforceProductToDataLayer(product), gaBreadcrumb);
        },
        1000,
        [product.id, gaBreadcrumb],
    );

    return (
        <div
            className={clsx(
                styles.productOverview,
                {
                    [styles.withMediaThumbnails]: images && images.length > 1,
                },
                className,
            )}
            {...rest}
        >
            {isCustomizableWithSpiff && <SpiffScript />}

            {/* Lets a keyboard skip the whole gallery rather than tab through every image. */}
            <a
                className={styles.productSkipLink}
                href={`#${PRODUCT_SUMMARY_ID}`}
                onClick={(event) => {
                    event.preventDefault();

                    // .replace() rather than .push(), so "Back" does not simply undo the skip.
                    router.replace(event.currentTarget.href);

                    // Bypassing the browser's own hash handling means moving focus ourselves.
                    focusSummary();
                }}
            >
                Spring billeder over
            </a>

            <div className={styles.productMain}>
                <ProductGallery
                    className={styles.productGallery}
                    images={images || []}
                    videos={product.c_videoLinks}
                >
                    {/* These product splash and tags are only visible exclusively on mobile as foreground for our gallery. */}
                    <ProductSplash className={styles.productGallerySplash} product={product} />

                    <ProductLabels className={styles.productGalleryLabels}>
                        <ProductTags />
                    </ProductLabels>
                </ProductGallery>

                {/* The id is the skip link's scroll target; focus goes to a child, not here. */}
                <div className={styles.productSummary} id={PRODUCT_SUMMARY_ID}>
                    <ProductLabels className={styles.productSummaryLabels}>
                        <ProductSplash product={product} />

                        <ProductTags />
                    </ProductLabels>

                    <div className={styles.productTrumpet}>
                        <h1 className={styles.productHeading}>
                            <ProductBrand className={styles.productBrand} />

                            {!!product.name && (
                                <Typography
                                    className={styles.productName}
                                    variant="paragraph-XXL"
                                    weight="medium"
                                >
                                    {replaceProductHyphensWithSlashes(product.name)}
                                </Typography>
                            )}
                        </h1>

                        <div className={styles.productToolbar}>
                            <ProductCompareButton className={styles.productCompareButton} />

                            <WishCloudButton className={styles.productWishCloud} />

                            <ProductWishlistButton
                                className={clsx(styles.productWishlistButton, styles.isActive)}
                            />
                        </div>
                    </div>

                    {!!product.c_synopsis && (
                        <Typography className={styles.productSynopsis} variant="paragraph-S" as="p">
                            {replaceProductHyphensWithSlashes(product.c_synopsis)}
                        </Typography>
                    )}

                    <YotpoReviewsSummary className={styles.productRating} />

                    <ProductPrices className={styles.productPrices} />

                    {!!campaignText && (
                        <Typography
                            className={styles.productCampaignText}
                            variant="paragraph-S"
                            as="p"
                        >
                            {campaignText}
                        </Typography>
                    )}

                    <ProductCampaignLifeCycle className={styles.productCampaignLifeCycle} />

                    <ProductInfoLabels className={styles.productInfoLabels} />

                    <ProductCampaignRedirect className={styles.productCampaignRedirects} />

                    <ProductVariants
                        className={styles.productVariants}
                        onVariantClick={onVariantClick}
                    />

                    <ProductPrelaunchCountdown className={styles.productPrelaunchCountdown} />
                    <ProductPromotionCountdown className={styles.productPromotionCountdown} />

                    {isCustomizableWithSpiff && (
                        <SpiffPersonalization className={styles.productPersonalization} />
                    )}

                    {isSoldOutPermanentlyOnline && (
                        <Typography
                            className={styles.permanentlyOutOfStockOnlineText}
                            variant="paragraph-S"
                            weight="medium"
                            as="p"
                        >
                            {translate('pdp.outOfStockOnlineOnlyBanner')}
                        </Typography>
                    )}

                    {isSoldOutPermanently ? (
                        <div className={styles.productSoldOut}>
                            <div className={styles.permanentlyOutOfStockBanner}>
                                <p className={styles.permanentlyOutOfStockBannerHeadline}>
                                    {translate('pdp.permanentlyOutOfStockBannerHeadline')}
                                </p>
                            </div>

                            <Typography
                                className={styles.permanentlyOutOfStockFootnote}
                                variant="paragraph-S"
                                as="div"
                            >
                                {translate('pdp.permanentlyOutOfStockBannerText')}
                            </Typography>
                        </div>
                    ) : (
                        <ProductActions ref={actionsRef} className={styles.productActions} />
                    )}

                    <ProductPrelaunchNotice className={styles.productPrelaunchNotice} />

                    <ProductExpressDelivery className={styles.productExpressDelivery} />
                    <ProductFreight className={styles.productFreight} />
                    <ProductUSP className={styles.productUSP} />

                    <ProductPaymentMethods className={styles.productPaymentMethods} />

                    <ProductRecommendations className={styles.productRecommendations} />
                </div>
            </div>

            <ProductTabs className={styles.productTabs} />

            <aside className={styles.productAside}>
                <ProductAIChat className={styles.productAIChat} />

                <ProductCategories
                    className={styles.productCategories}
                    onCategoryClick={onCategoryClick}
                />
            </aside>

            {!isSoldOutPermanently && (
                <ProductSticky image={images?.at(0)} referencePoint={actionsRef} />
            )}

            <ProductDialogs />
        </div>
    );
};

const ProductLabels: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
    children,
    className,
    ...rest
}) => {
    return (
        <div className={clsx(styles.productLabels, className)} {...rest}>
            {children}
        </div>
    );
};
